import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  catchError,
  firstValueFrom,
  map,
  Observable,
  retry,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import { ErpApiError, mapErpError } from './erp-api.errors';
import { ERP_API_URL } from './erp-api.tokens';
import {
  SupplierApiDownload,
  SupplierCreateRequest,
  SupplierDto,
  SupplierEditForm,
  SupplierFilterSelects,
  SupplierFormSelects,
  SupplierGridDto,
  SupplierListQuery,
  SupplierListResult,
  SupplierSelectDto,
  SupplierToneDto,
  SupplierUpdateRequest,
  SupplierAddressDto,
} from './suppliers-api.types';

const REQUEST_TIMEOUT_MS = 15_000;
const LIST_PAGE_SIZE = 100;
const SUPPLIERS_PATH = '/suppliers';

@Injectable({ providedIn: 'root' })
export class SuppliersApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(ERP_API_URL).replace(/\/+$/, '')}${SUPPLIERS_PATH}`;

  list(query: SupplierListQuery = {}): Observable<SupplierListResult> {
    return this.safeList(
      this.http.get<unknown>(this.baseUrl, {
        params: supplierListParams(query),
      }),
      unwrapList,
    );
  }

  formSelects(): Promise<SupplierFormSelects> {
    return this.safeGet(`${this.baseUrl}/form-selects`, unwrapFormSelects);
  }

  /** Catálogos del alta. Mismo DTO que `form-selects`; el contrato lo reserva al flujo create → store. */
  prepareCreate(): Promise<SupplierFormSelects> {
    return this.safeGet(`${this.baseUrl}/create`, unwrapFormSelects);
  }

  filterSelects(): Promise<SupplierFilterSelects> {
    return this.safeGet(`${this.baseUrl}/filter-selects`, unwrapFilterSelects);
  }

  states(countryUuid: string): Promise<SupplierSelectDto[]> {
    return this.safeGet(
      `${this.baseUrl}/form-selects/states`,
      unwrapSelectList,
      new HttpParams().set('countryUuid', countryUuid),
    );
  }

  municipalities(stateUuid: string): Promise<SupplierSelectDto[]> {
    return this.safeGet(
      `${this.baseUrl}/form-selects/municipalities`,
      unwrapSelectList,
      new HttpParams().set('stateUuid', stateUuid),
    );
  }

  getById(uuid: string): Promise<SupplierDto> {
    return this.safeGet(`${this.baseUrl}/${uuid}`, unwrapSupplier);
  }

  editForm(uuid: string): Promise<SupplierEditForm> {
    return this.safeGet(`${this.baseUrl}/${uuid}/edit`, unwrapEditForm);
  }

  create(payload: SupplierCreateRequest): Promise<SupplierDto> {
    return this.mutate(this.http.post<unknown>(this.baseUrl, payload), unwrapSupplier);
  }

  update(uuid: string, payload: SupplierUpdateRequest): Promise<SupplierDto> {
    return this.mutate(
      this.http.put<unknown>(`${this.baseUrl}/${uuid}`, payload),
      unwrapSupplier,
    );
  }

  activate(uuid: string): Promise<void> {
    return this.mutate(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/activate`, null),
      () => undefined,
    );
  }

  inactivate(uuid: string): Promise<void> {
    return this.mutate(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/inactivate`, null),
      () => undefined,
    );
  }

  remove(uuid: string): Promise<void> {
    return this.mutate(
      this.http.delete<unknown>(`${this.baseUrl}/${uuid}`),
      () => undefined,
    );
  }

  exportExcel(query: SupplierListQuery = {}): Promise<SupplierApiDownload> {
    return this.download(
      `${this.baseUrl}/excel`,
      supplierListParams(query, false),
      'proveedores.xlsx',
    );
  }

  private safeList<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Observable<T> {
    return source.pipe(
      timeout(REQUEST_TIMEOUT_MS),
      retry({ count: 1, delay: error => retryDelay(error) }),
      map(project),
      catchError(error => throwError(() => mapErpError(error))),
    );
  }

  private safeGet<T>(
    url: string,
    project: (body: unknown) => T,
    params?: HttpParams,
  ): Promise<T> {
    return this.request(
      this.http
        .get<unknown>(url, { params })
        .pipe(retry({ count: 1, delay: error => retryDelay(error) })),
      project,
    );
  }

  private mutate<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Promise<T> {
    return this.request(source, project);
  }

  private async request<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Promise<T> {
    try {
      const body = await firstValueFrom(source.pipe(timeout(REQUEST_TIMEOUT_MS)));
      return project(body);
    } catch (error) {
      throw mapErpError(error);
    }
  }

  private async download(
    url: string,
    params: HttpParams,
    fallbackFilename: string,
  ): Promise<SupplierApiDownload> {
    try {
      const response = await firstValueFrom(
        this.http
          .get(url, { params, observe: 'response', responseType: 'blob' })
          .pipe(
            timeout(REQUEST_TIMEOUT_MS),
            retry({ count: 1, delay: error => retryDelay(error) }),
          ),
      );
      return toDownload(response, fallbackFilename);
    } catch (error) {
      throw mapErpError(error);
    }
  }
}

function retryDelay(error: unknown): Observable<number> {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 429) {
      const retryAfter = Number(error.headers.get('Retry-After'));
      const seconds =
        Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 1;
      const jitterMs = Math.floor(Math.random() * 250);
      return timer(seconds * 1000 + jitterMs);
    }
    if (error.status >= 500 || error.status === 0) return timer(300);
  }
  return throwError(() => error);
}

function supplierListParams(
  query: SupplierListQuery,
  includePagination = true,
): HttpParams {
  let params = new HttpParams();
  if (includePagination) {
    params = params
      .set('page', String(query.page ?? 0))
      .set('size', String(query.size ?? LIST_PAGE_SIZE));
  }
  if (query.name) params = params.set('filters[name]', query.name);
  if (query.rfc) params = params.set('filters[rfc]', query.rfc);
  if (query.curp) params = params.set('filters[curp]', query.curp);
  params = setCsv(params, 'select[type]', query.typeUuids);
  params = setCsv(params, 'select[paymentCondition]', query.paymentConditionUuids);
  params = setCsv(params, 'select[status]', query.statusUuids);
  if (query.sortBy) params = params.set('sortBy', query.sortBy);
  if (query.order) params = params.set('order', query.order);
  return params;
}

function setCsv(
  params: HttpParams,
  key: string,
  values: readonly string[] | undefined,
): HttpParams {
  return values?.length ? params.set(key, values.join(',')) : params;
}

function toDownload(
  response: HttpResponse<Blob>,
  fallbackFilename: string,
): SupplierApiDownload {
  if (!response.body) throw new ErpApiError('unexpected', 0);
  return {
    blob: response.body,
    filename:
      contentDispositionFilename(response.headers.get('Content-Disposition')) ??
      fallbackFilename,
  };
}

function contentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.replace(/^"|"$/g, ''));
    } catch {
      return encoded.replace(/^"|"$/g, '');
    }
  }
  return header.match(/filename="?([^";]+)"?/i)?.[1] ?? null;
}

function unwrapData(body: unknown): unknown {
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: unknown }).data;
  }
  return body;
}

function unwrapList(body: unknown): SupplierListResult {
  const page = asRecord(unwrapData(body));
  const nested = page?.['data'];
  if (!Array.isArray(nested)) throw new ErpApiError('unexpected', 0);
  const items = nested.map(item => {
    const row = normalizeGrid(item);
    if (!row) throw new ErpApiError('unexpected', 0);
    return row;
  });
  const pagination = asRecord(page?.['pagination']);
  const totalRecords = pagination?.['totalRecords'];
  return {
    items,
    total: typeof totalRecords === 'number' ? totalRecords : items.length,
  };
}

function unwrapSupplier(body: unknown): SupplierDto {
  const supplier = normalizeDetail(unwrapData(body));
  if (!supplier) throw new ErpApiError('unexpected', 0);
  return supplier;
}

function unwrapFormSelects(body: unknown): SupplierFormSelects {
  const data = asRecord(unwrapData(body));
  if (!data) throw new ErpApiError('unexpected', 0);
  return {
    personTypes: selectList(data['personTypes']),
    societyTypes: selectList(data['societyTypes']),
    paymentConditions: selectList(data['paymentConditions']),
    countries: selectList(data['countries']),
  };
}

function unwrapFilterSelects(body: unknown): SupplierFilterSelects {
  const data = asRecord(unwrapData(body));
  if (!data) throw new ErpApiError('unexpected', 0);
  return {
    statuses: selectList(data['statuses']),
    personTypes: selectList(data['personTypes']),
    paymentConditions: selectList(data['paymentConditions']),
  };
}

function unwrapSelectList(body: unknown): SupplierSelectDto[] {
  const data = unwrapData(body);
  return selectList(Array.isArray(data) ? data : asRecord(data)?.['data']);
}

function unwrapEditForm(body: unknown): SupplierEditForm {
  const data = asRecord(unwrapData(body));
  const supplier = data ? normalizeDetail(data['supplier']) : null;
  const selects = asRecord(data?.['selects']);
  if (!supplier || !selects) throw new ErpApiError('unexpected', 0);
  return {
    supplier,
    selects: {
      personTypes: selectList(selects['personTypes']),
      societyTypes: selectList(selects['societyTypes']),
      paymentConditions: selectList(selects['paymentConditions']),
      countries: selectList(selects['countries']),
    },
  };
}

function selectList(value: unknown): SupplierSelectDto[] {
  if (!Array.isArray(value)) throw new ErpApiError('unexpected', 0);
  return value.map(item => {
    const option = selectDto(item);
    if (!option) throw new ErpApiError('unexpected', 0);
    return option;
  });
}

function normalizeGrid(value: unknown): SupplierGridDto | null {
  const record = asRecord(value);
  if (!record) return null;
  const uuid = stringField(record, 'uuid');
  const id = numberField(record, 'id');
  const name = stringField(record, 'name');
  const type = selectDto(record['type']);
  const rfc = stringField(record, 'rfc');
  const paymentCondition = selectDto(record['paymentCondition']);
  const status = toneDto(record['status']);
  if (!uuid || id === null || !name || !type || !rfc || !paymentCondition || !status) {
    return null;
  }
  const curp = nullableString(record['curp']);
  const creditLimit = nullableNumber(record['creditLimit']);
  const creditTermDays = nullableNumber(record['creditTermDays']);
  if (curp === undefined || creditLimit === undefined || creditTermDays === undefined) {
    return null;
  }
  return {
    uuid,
    id,
    name,
    type,
    rfc,
    curp,
    paymentCondition,
    creditLimit,
    creditTermDays: creditTermDays === null ? null : Math.trunc(creditTermDays),
    status,
  };
}

function normalizeDetail(value: unknown): SupplierDto | null {
  const grid = normalizeGrid(value);
  const record = asRecord(value);
  if (!grid || !record) return null;
  const createdAt = stringField(record, 'createdAt');
  const updatedAt = stringField(record, 'updatedAt');
  const businessName = nullableString(record['businessName']);
  const societyType = nullableSelect(record['societyType']);
  const notes = nullableString(record['notes']);
  const address = nullableAddress(record['address']);
  if (!createdAt || !updatedAt || businessName === undefined || societyType === undefined) {
    return null;
  }
  if (notes === undefined || address === undefined) return null;
  return {
    ...grid,
    businessName,
    societyType,
    notes,
    address,
    createdAt,
    updatedAt,
  };
}

function nullableAddress(value: unknown): SupplierAddressDto | null | undefined {
  if (value == null) return null;
  const record = asRecord(value);
  if (!record) return undefined;
  const street = stringField(record, 'street');
  const exteriorNumber = stringField(record, 'exteriorNumber');
  const interiorNumber = nullableString(record['interiorNumber']);
  const colony = stringField(record, 'colony');
  const zipCode = stringField(record, 'zipCode');
  const country = selectDto(record['country']);
  const state = selectDto(record['state']);
  const municipality = selectDto(record['municipality']);
  if (
    !street ||
    !exteriorNumber ||
    interiorNumber === undefined ||
    !colony ||
    !zipCode ||
    !country ||
    !state ||
    !municipality
  ) {
    return undefined;
  }
  return {
    street,
    exteriorNumber,
    interiorNumber,
    colony,
    zipCode,
    country,
    state,
    municipality,
  };
}

function nullableSelect(value: unknown): SupplierSelectDto | null | undefined {
  if (value == null) return null;
  return selectDto(value) ?? undefined;
}

function nullableString(value: unknown): string | null | undefined {
  if (value == null) return null;
  return typeof value === 'string' ? value : undefined;
}

function nullableNumber(value: unknown): number | null | undefined {
  if (value == null) return null;
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function selectDto(value: unknown): SupplierSelectDto | null {
  const record = asRecord(value);
  if (!record) return null;
  const uuid = stringField(record, 'uuid');
  const name = stringField(record, 'name');
  return uuid && name ? { uuid, name } : null;
}

function toneDto(value: unknown): SupplierToneDto | null {
  const record = asRecord(value);
  if (!record) return null;
  const name = stringField(record, 'name');
  const toneValue = stringField(record, 'value');
  const tone = stringField(record, 'tone');
  return name && toneValue && tone ? { name, value: toneValue, tone } : null;
}

function stringField(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

function numberField(record: Record<string, unknown>, key: string): number | null {
  const value = record[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}
